// Shared pickers: searchable dropdowns over existing list endpoints. New
// pages (and the payments hub) use these instead of raw UUID text inputs — an
// id the user has to copy out of another table is a data-entry bug waiting to
// happen.
//
// Every picker surfaces loading, failure and retry through the shared
// `SearchSelect`; a failed lookup never renders as an empty list.
import { formatMoney } from "@sylviq/ui-core";
import {
  SearchSelect,
  type SearchSelectOption,
} from "@sylviq/ui-react-web/components/ui/search-select";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  type Bill,
  type ChartAccount,
  type DirectoryContact,
  type Invoice,
  listBills,
  listChartAccounts,
  listContacts,
  listInvoices,
  listTaxRates,
  rateBpLabel,
  type TaxRate,
} from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

interface PickerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  /** Accessible name when the picker is not wrapped in a `<label>`. */
  ariaLabel?: string;
}

interface OptionsState<T> {
  options: T[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

function useOptions<T>(load: () => Promise<T[]>): OptionsState<T> {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .then((rows) => {
        if (!cancelled) {
          setOptions(rows);
          setLoading(false);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(toErrorMessage(cause));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [load, attempt]);
  return { options, loading, error, reload: () => setAttempt((n) => n + 1) };
}

const ACCOUNT_GROUP_ORDER = [
  "asset",
  "liability",
  "equity",
  "revenue",
  "expense",
] as const;

const ACCOUNT_GROUP_LABELS: Record<string, string> = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  revenue: "Income",
  expense: "Expenses",
};

function accountGroup(accountType: string | undefined): string {
  const key = (accountType ?? "").trim().toLowerCase();
  if (!key) return "Other accounts";
  return (
    ACCOUNT_GROUP_LABELS[key] ??
    key.charAt(0).toUpperCase() + key.slice(1).replaceAll("_", " ")
  );
}

function accountSortKey(accountType: string | undefined): number {
  const index = ACCOUNT_GROUP_ORDER.indexOf(
    (accountType ?? "").trim().toLowerCase() as (typeof ACCOUNT_GROUP_ORDER)[number],
  );
  return index === -1 ? ACCOUNT_GROUP_ORDER.length : index;
}

/** Chart-of-accounts picker for line forms (invoices, credit notes,
 * recurring templates). Options show code + name grouped by account type;
 * a failed or still-loading lookup is visible, never an empty list. */
export function AccountPicker({
  id,
  value,
  onChange,
  disabled,
  required,
  ariaLabel,
}: PickerProps) {
  const { options, loading, error, reload } =
    useOptions<ChartAccount>(listChartAccounts);
  const accountOptions: SearchSelectOption[] = options
    .filter((account) => account.active)
    .sort(
      (a, b) =>
        accountSortKey(a.account_type) - accountSortKey(b.account_type) ||
        a.code.localeCompare(b.code),
    )
    .map((account) => ({
      value: account.id,
      label: `${account.code} — ${account.name}`,
      group: accountGroup(account.account_type),
      keywords: `${account.code} ${account.name} ${account.account_type}`,
    }));
  return (
    <SearchSelect
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      options={accountOptions}
      placeholder="Pick an account…"
      searchPlaceholder="Search code or name…"
      emptyMessage="No account matches"
      loading={loading}
      error={error}
      onRetry={reload}
      disabled={disabled}
      required={required}
    />
  );
}

/** Tax-rate picker for invoice, bill and quote lines. Offers an explicit
 * "No tax" choice plus each active organization rate as
 * `name — rateBpLabel(rate_bp)`. A failed or still-loading lookup is visible,
 * never an empty list. The server re-computes tax from the chosen rate id. */
export function TaxRatePicker({
  id,
  value,
  onChange,
  disabled,
  required,
  ariaLabel,
}: PickerProps) {
  const { options, loading, error, reload } = useOptions<TaxRate>(listTaxRates);
  const rateOptions: SearchSelectOption[] = [
    { value: "", label: "No tax", keywords: "none no tax zero exempt" },
    ...options
      .filter((rate) => rate.is_active || rate.id === value)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((rate) => ({
        value: rate.id,
        label: `${rate.name} — ${rateBpLabel(rate.rate_bp)}`,
        keywords: `${rate.name} ${rateBpLabel(rate.rate_bp)}`,
      })),
  ];
  return (
    <SearchSelect
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      options={rateOptions}
      placeholder="No tax"
      searchPlaceholder="Search tax rates…"
      emptyMessage="No tax rate matches"
      loading={loading}
      error={error}
      onRetry={reload}
      disabled={disabled}
      required={required}
    />
  );
}

/** Invoice picker for drawers that target one invoice (credit notes,
 * prepayment applications, revenue schedules). Optionally narrowed to one
 * customer's invoices. */
export function InvoicePicker({
  id,
  value,
  onChange,
  disabled,
  required,
  ariaLabel,
  customerName,
  crmContactId,
  legalEntityId,
}: PickerProps & {
  customerName?: string;
  crmContactId?: string;
  legalEntityId?: string;
}) {
  const { options, loading, error, reload } =
    useOptions<Invoice>(listInvoices);
  const entityScoped = legalEntityId
    ? options.filter((invoice) => invoice.legal_entity_id === legalEntityId)
    : options;
  const narrowed = crmContactId
    ? entityScoped.filter((invoice) => invoice.crm_contact_id === crmContactId)
    : customerName
      ? entityScoped.filter(
          (invoice) =>
            invoice.customer_name.toLowerCase() ===
            customerName.trim().toLowerCase(),
        )
      : entityScoped;
  const invoiceOptions: SearchSelectOption[] = narrowed.map((invoice) => ({
    value: invoice.id,
    label: `${invoice.invoice_number} — ${invoice.customer_name} (${formatMoney({ cents: invoice.total_minor, currency: "USD" })})`,
    keywords: `${invoice.invoice_number} ${invoice.customer_name}`,
  }));
  return (
    <SearchSelect
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      options={invoiceOptions}
      placeholder="Pick an invoice…"
      searchPlaceholder="Search number or customer…"
      emptyMessage="No invoice matches"
      loading={loading}
      error={error}
      onRetry={reload}
      disabled={disabled}
      required={required}
    />
  );
}

/** Bill picker for the payments hub Paid tab and supplier-credit forms. */
export function BillPicker({
  id,
  value,
  onChange,
  disabled,
  required,
  ariaLabel,
}: PickerProps) {
  const { options, loading, error, reload } = useOptions<Bill>(listBills);
  const billOptions: SearchSelectOption[] = options.map((bill) => ({
    value: bill.id,
    label: `${bill.bill_number} — ${bill.supplier_name} (${formatMoney({ cents: bill.total_cents, currency: "USD" })})`,
    keywords: `${bill.bill_number} ${bill.supplier_name}`,
  }));
  return (
    <SearchSelect
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      options={billOptions}
      placeholder="Pick a bill…"
      searchPlaceholder="Search number or supplier…"
      emptyMessage="No bill matches"
      loading={loading}
      error={error}
      onRetry={reload}
      disabled={disabled}
      required={required}
    />
  );
}

/** Contact picker over the accounting contact directory. CRM owns identity;
 * Accounting can now create and list the narrow contact record it needs, so a
 * new customer or supplier is immediately selectable here. An empty directory
 * links out to Contacts instead of rendering a dead dropdown. */
export function LinkedContactPicker({
  id,
  value,
  onChange,
  disabled,
  ariaLabel,
}: PickerProps) {
  const { options, loading, error, reload } =
    useOptions<DirectoryContact>(listContacts);
  const contactOptions: SearchSelectOption[] = options.map((contact) => ({
    value: contact.id,
    label: contact.display_name,
    keywords: [contact.first_name, contact.last_name, contact.email, contact.phone]
      .filter(Boolean)
      .join(" "),
  }));
  if (!loading && !error && contactOptions.length === 0) {
    return (
      <p className="text-xs text-[var(--muted-foreground)]">
        No contacts yet —{" "}
        <Link to="/contacts" className="underline">
          create one on the Contacts page
        </Link>{" "}
        first, or leave this blank.
      </p>
    );
  }
  return (
    <SearchSelect
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      options={contactOptions}
      placeholder="No contact"
      searchPlaceholder="Search contacts…"
      emptyMessage="No contact matches"
      loading={loading}
      error={error}
      onRetry={reload}
      disabled={disabled}
    />
  );
}
